import { useState, useMemo, useEffect } from 'react';

// Lib
import { useAppSelector, useAppDispatch } from 'lib/hooks/useDispatch';

// Store
import { RootState } from '../../store';

// Actions
import { addTutorialKey } from 'reducers/settings';

export const useRegisterTutorialKey = () => {
	const dispatch = useAppDispatch();
	return (
		key: string,
		condition?: () => boolean,
		priority?: 'low' | 'normal' | 'high',
		group?: 'structure' | 'header' | 'general'
	) => {
		dispatch(addTutorialKey({ key, condition, priority, group }));
	};
};

export const useCollectedTutorialKeys = () => {
	return useAppSelector(selectActiveTutorialKeys);
};

export const useSeenTutorialKeys = () => {
	return useAppSelector((s) => s.settings.tutorial.seen);
};

export function useTutorialSequence(keys: string[] = []) {
	const seen = useSeenTutorialKeys();
	const normalizedKeys = useMemo(() => keys.slice(), [keys]);

	const findNext = () =>
		normalizedKeys.find(k => !seen.includes(k)) ?? null;

	const [current, setCurrent] = useState<string | null>(() => findNext());

	useEffect(() => {
		setCurrent(findNext());
	}, [JSON.stringify(normalizedKeys), seen]);

	return current;
}

export const selectActiveTutorialKeys = (state: RootState) => {
	const { keys, seen } = state.settings.tutorial;
	const groupOrder = ['general', 'header'];

	let activeGroup: string | null = null;
	for (const group of groupOrder) {
		const groupKeys = keys.filter(k => (k.group || 'general') === group);
		if (!groupKeys.length) continue;
		const allSeen = groupKeys.every(k => seen.includes(k.key));
		if (!allSeen) {
			activeGroup = group;
			break;
		}
	}

	const activeKeys = keys
		.filter(k => (k.group || 'general') === activeGroup)
		.filter(k => !k.condition || k.condition())
		.sort((a, b) => {
			const priorityOrder = { high: 0, normal: 1, low: 2 };
			return priorityOrder[a.priority || 'normal'] - priorityOrder[b.priority || 'normal'];
		});

	return activeKeys.map(k => k.key);
};


export const useTutorialRunner = (keys: string[] = []) => {
	const currentKey = useTutorialSequence(keys);
	const [readyKey, setReadyKey] = useState<string | null>(null);

	useEffect(() => {
		type TimeoutId = ReturnType<typeof setTimeout>;
		if (!currentKey) return setReadyKey(null);

		let timeoutId: TimeoutId;
		let cancelled = false;

		requestAnimationFrame(() => {
			timeoutId = setTimeout(() => {
				if (!cancelled) {
					setReadyKey(currentKey);
					cancelled = true;
				}
			}, 600);
		});

		return () => {
			clearTimeout(timeoutId);
		};
	}, [currentKey]);

	return readyKey;
};
